import React, { useState } from 'react';

const ATVSelection = ({ tour, bookingData, onComplete, onBack }) => {
    const [numSingle, setNumSingle] = useState(0);
    const [numDouble, setNumDouble] = useState(0);

    // Get unified ATV availability
    const selectedSlot = bookingData.selectedSlot || {};
    const atvsAvailable = selectedSlot.atvs_available ?? 8;
    const totalAtvs = selectedSlot.fleet_info?.total_atvs ?? 8;

    // Pricing
    const basePrice = selectedSlot.base_atv_price || tour.base_atv_price || tour.price;
    const sharedFee = selectedSlot.shared_ride_fee || tour.shared_ride_fee || 0;

    const singlePrice = basePrice;
    const doublePrice = basePrice + sharedFee;

    // Calculate totals
    const totalAtvCount = numSingle + numDouble;
    const singleTotal = numSingle * singlePrice;
    const doubleTotal = numDouble * doublePrice;
    const grandTotal = singleTotal + doubleTotal;

    // Validation
    const exceedsCapacity = totalAtvCount > atvsAvailable;
    const canContinue = totalAtvCount > 0 && !exceedsCapacity;

    const handleContinue = () => {
        if (!canContinue) return;

        const rideType = numSingle > 0 && numDouble > 0 ? 'mixed'
            : numSingle > 0 ? 'single'
                : 'shared';

        onComplete({
            rideType: rideType,
            numAtvs: totalAtvCount,
            numSingleAtvs: numSingle,
            numDoubleAtvs: numDouble,
            numRiders: totalAtvCount, // One rider per ATV
            numPassengers: 0, // Added in next step for double ATVs
            totalAmount: grandTotal,
            pricePerAtv: totalAtvCount > 0 ? grandTotal / totalAtvCount : 0,
            basePrice: basePrice,
            sharedFee: sharedFee,
            singlePrice: singlePrice,
            doublePrice: doublePrice
        });
    };

    return (
        <div className="min-h-screen bg-gray-900 pt-8">
            <div className="max-w-4xl mx-auto px-4">
                {/* Header */}
                <div className="text-center mb-8">
                    <h2 className="text-4xl font-playfair font-bold text-white mb-2">
                        Plan Your Experience
                    </h2>
                    <p className="text-gray-400">Select your ATVs - mix single and double as needed</p>
                </div>

                {/* Fleet Availability */}
                <div className="bg-blue-500/10 border border-blue-500 rounded-xl p-4 mb-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-blue-200 font-medium">
                                🏍️ {atvsAvailable} of {totalAtvs} ATVs Available
                            </span>
                        </div>
                        {totalAtvCount > 0 && (
                            <span className="text-blue-300 text-sm">
                                Selected: {totalAtvCount} / {atvsAvailable}
                            </span>
                        )}
                    </div>
                </div>

                {/* ✅ ADD THIS: Enhanced Selection Summary */}
                {totalAtvCount > 0 && (
                    <div className={`rounded-xl p-4 mb-8 ${numSingle > 0 && numDouble > 0
                            ? 'bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-green-500/10 border border-purple-500'
                            : numSingle > 0
                                ? 'bg-blue-500/10 border border-blue-500'
                                : 'bg-green-500/10 border border-green-500'
                        }`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="flex-shrink-0">
                                    {numSingle > 0 && numDouble > 0 ? (
                                        <div className="flex items-center gap-1">
                                            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">
                                                {numSingle}
                                            </div>
                                            <span className="text-white text-xl">+</span>
                                            <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center text-white font-bold text-sm">
                                                {numDouble}
                                            </div>
                                        </div>
                                    ) : numSingle > 0 ? (
                                        <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center">
                                            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                        </div>
                                    ) : (
                                        <div className="w-12 h-12 rounded-full bg-green-600 flex items-center justify-center">
                                            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <p className="font-bold text-white text-lg">
                                        {numSingle > 0 && numDouble > 0
                                            ? `Mixed Selection`
                                            : numSingle > 0
                                                ? `Single Ride${numSingle > 1 ? 's' : ''} Only`
                                                : `Shared Ride${numDouble > 1 ? 's' : ''} Only`
                                        }
                                    </p>
                                    <p className="text-sm text-gray-300">
                                        {numSingle > 0 && numDouble > 0
                                            ? `${numSingle} single + ${numDouble} double ATV${totalAtvCount > 1 ? 's' : ''}`
                                            : numSingle > 0
                                                ? `${numSingle} rider${numSingle > 1 ? 's' : ''} · Solo adventure${numSingle > 1 ? 's' : ''}`
                                                : `${numDouble} ATV${numDouble > 1 ? 's' : ''} · Up to ${numDouble * 2} people`
                                        }
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-2xl font-bold text-emerald-400">${grandTotal.toFixed(2)}</p>
                                <p className="text-xs text-gray-400">${(grandTotal / totalAtvCount).toFixed(2)} per ATV</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* ATV Selectors */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                    {/* Single ATVs */}
                    <div className="bg-gray-800 rounded-2xl p-6 border-2 border-gray-700">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-4xl">🏍️</span>
                                    <h3 className="text-2xl font-bold text-white">Single ATVs</h3>
                                </div>
                                <p className="text-gray-400 text-sm">1 Person - rider only</p>
                                <p className="text-gray-500 text-xs">Physical driver's license required</p>
                            </div>
                            <div className="text-right">
                                <div className="text-2xl font-bold text-emerald-400">
                                    ${singlePrice.toFixed(2)}
                                </div>
                                <div className="text-xs text-gray-400">per ATV</div>
                            </div>
                        </div>

                        {/* Quantity Selector */}
                        <div className="flex items-center gap-4 bg-gray-700 rounded-lg p-4">
                            <button
                                type="button"
                                onClick={() => setNumSingle(Math.max(0, numSingle - 1))}
                                className="w-10 h-10 rounded-lg bg-gray-600 hover:bg-gray-500 text-white text-xl font-bold transition-colors"
                            >
                                −
                            </button>

                            <div className="flex-1 text-center">
                                <div className="text-3xl font-bold text-white">{numSingle}</div>
                                <div className="text-sm text-gray-400">
                                    {numSingle === 1 ? 'ATV' : 'ATVs'}
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => setNumSingle(Math.min(atvsAvailable - numDouble, numSingle + 1))}
                                disabled={totalAtvCount >= atvsAvailable}
                                className="w-10 h-10 rounded-lg bg-gray-600 hover:bg-gray-500 text-white text-xl font-bold transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                +
                            </button>
                        </div>

                        {numSingle > 0 && (
                            <div className="mt-4 p-3 bg-gray-700 rounded-lg">
                                <div className="flex justify-between text-sm text-gray-300">
                                    <span>{numSingle} × ${singlePrice.toFixed(2)}</span>
                                    <span className="font-bold text-white">${singleTotal.toFixed(2)}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Double ATVs */}
                    <div className="bg-gray-800 rounded-2xl p-6 border-2 border-gray-700">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-4xl">👥</span>
                                    <h3 className="text-2xl font-bold text-white">Double ATVs</h3>
                                </div>
                                <p className="text-gray-400 text-sm">2 People - rider + passenger</p>
                                <p className="text-gray-500 text-xs">Share the experience</p>
                            </div>
                            <div className="text-right">
                                <div className="text-2xl font-bold text-emerald-400">
                                    ${doublePrice.toFixed(2)}
                                </div>
                                <div className="text-xs text-gray-400">per ATV</div>
                                {/* {sharedFee > 0 && (
                                    <div className="text-xs text-gray-500">+${sharedFee.toFixed(2)} shared fee</div>
                                )} */}
                            </div>
                        </div>

                        {/* Quantity Selector */}
                        <div className="flex items-center gap-4 bg-gray-700 rounded-lg p-4">
                            <button
                                type="button"
                                onClick={() => setNumDouble(Math.max(0, numDouble - 1))}
                                className="w-10 h-10 rounded-lg bg-gray-600 hover:bg-gray-500 text-white text-xl font-bold transition-colors"
                            >
                                −
                            </button>

                            <div className="flex-1 text-center">
                                <div className="text-3xl font-bold text-white">{numDouble}</div>
                                <div className="text-sm text-gray-400">
                                    {numDouble === 1 ? 'ATV' : 'ATVs'}
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => setNumDouble(Math.min(atvsAvailable - numSingle, numDouble + 1))}
                                disabled={totalAtvCount >= atvsAvailable}
                                className="w-10 h-10 rounded-lg bg-gray-600 hover:bg-gray-500 text-white text-xl font-bold transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                +
                            </button>
                        </div>

                        {numDouble > 0 && (
                            <div className="mt-4 p-3 bg-gray-700 rounded-lg">
                                <div className="flex justify-between text-sm text-gray-300">
                                    <span>{numDouble} × ${doublePrice.toFixed(2)}</span>
                                    <span className="font-bold text-white">${doubleTotal.toFixed(2)}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Error Messages */}
                {exceedsCapacity && (
                    <div className="bg-red-500/20 border border-red-500 rounded-xl p-4 mb-8">
                        <p className="text-red-300">
                            ⚠️ You've selected {totalAtvCount} ATVs but only {atvsAvailable} are available. Please reduce your selection.
                        </p>
                    </div>
                )}

                {/* Price Summary */}
                {totalAtvCount > 0 && (
                    <div className="bg-gray-800 rounded-2xl p-6 mb-8">
                        <h3 className="text-xl font-bold text-white mb-4">Price Summary</h3>
                        <div className="space-y-2">
                            {numSingle > 0 && (
                                <div className="flex justify-between text-gray-300">
                                    <span>{numSingle} × Single ATVs</span>
                                    <span>${singleTotal.toFixed(2)}</span>
                                </div>
                            )}
                            {numDouble > 0 && (
                                <div className="flex justify-between text-gray-300">
                                    <span>{numDouble} × Double ATVs</span>
                                    <span>${doubleTotal.toFixed(2)}</span>
                                </div>
                            )}

                            <div className="border-t border-gray-700 pt-2 flex justify-between text-2xl font-bold text-emerald-400">
                                <span>Total</span>
                                <span>${grandTotal.toFixed(2)}</span>
                            </div>

                            {numDouble > 0 && (
                                <div className="text-sm text-gray-400 italic mt-2">
                                    💡 Add passenger details in the next step
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-4">
                    <button
                        onClick={onBack}
                        className="px-8 py-4 bg-gray-700 hover:bg-gray-600 rounded-lg font-bold text-lg transition-colors"
                    >
                        ← Back
                    </button>
                    <button
                        onClick={handleContinue}
                        disabled={!canContinue}
                        className={`flex-1 px-8 py-4 rounded-lg font-bold text-lg transition-colors ${canContinue
                                ? 'bg-emerald-600 hover:bg-emerald-700'
                                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                            }`}
                    >
                        Continue to Rider Details →
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ATVSelection;